import {
    useState,
    useCallback,
    useEffect,
    InputHTMLAttributes,
    ChangeEventHandler,
    forwardRef,
} from "react"

import { PHONE_ERRORS_TEXT } from "modules/Booking/FormPage/common/constants"
import { parsePhoneNumber } from "utils/phone-number"

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    phoneValidationError: (v: string) => void
    setPhoneNumber: (v: string) => void
}

function extractProps(props: Props) {
    const { defaultValue, phoneValidationError, setPhoneNumber, ...otherProps } = props
    return {
        defaultValue: typeof defaultValue === "undefined" ? "" : String(defaultValue),
        htmlInputProps: otherProps,
        setPhoneNumber,
        phoneValidationError,
    }
}

function initialState(defaultValue: string) {
    const defaultPhoneNumber = parsePhoneNumber(defaultValue)
    if (!defaultPhoneNumber.isValid) {
        return {
            error: defaultValue ? PHONE_ERRORS_TEXT.international : PHONE_ERRORS_TEXT.empty,
            value: defaultPhoneNumber.raw,
        }
    }
    return {
        error: "",
        value: defaultPhoneNumber.internationalFormat,
    }
}

export const PhoneInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
    const { htmlInputProps, defaultValue, setPhoneNumber, phoneValidationError } =
        extractProps(props)
    const [value, setValue] = useState("")
    const [error, setError] = useState("")

    useEffect(() => {
        phoneValidationError(error)
    }, [error])

    useEffect(() => {
        const { error, value } = initialState(defaultValue)
        setError(error)
        setValue(value ?? "")
    }, [defaultValue])

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
        const phoneNumber = parsePhoneNumber(e.target.value.trim())
        if (!phoneNumber.isValid) {
            setValue(phoneNumber.raw ?? "")
            setError(phoneNumber.raw ? PHONE_ERRORS_TEXT.international : PHONE_ERRORS_TEXT.empty)
            return
        }
        setError("")
        setValue(phoneNumber.internationalFormat ?? "")
        setPhoneNumber(phoneNumber.internationalFormat ?? value)
    }, [])

    return (
        <input
            type="tel"
            value={value}
            onChange={handleChange}
            ref={ref}
            className={`form-control ${htmlInputProps.className ?? ""}`}
            title={error ? error : undefined}
            {...htmlInputProps}
        />
    )
})

PhoneInput.displayName = "PhoneInput"
